import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import toast from 'react-hot-toast';
import { Target, Plus, Edit2, Trash2, Check, TrendingUp, TrendingDown, Minus, Eye, EyeOff, ArrowLeftRight } from 'lucide-react';
import { GoalProgressModal } from '../client/goals/GoalProgressModal';
import { Modal } from '../admin/Modal';
import './GoalManagement.css';

interface Goal {
  goal_id: string;
  goal_name: string;
  goal_value: number;
  goal_type: 'over' | 'under' | 'target' | 'range' | 'boolean';
  variance_percent?: number;
  floor_value?: number;
  unit: string;
  health_data_field: string;
  category: 'nutrition' | 'fitness' | 'wellness' | 'supplements' | 'other';
  status: 'enabled' | 'disabled';
  visibility: 'hidden' | 'visible';
  highlight_for_client?: boolean;  // Show on client's "This Week" tile
  date_modified?: string;
  created_date: string;
}

interface GoalTemplate {
  template_id: string;
  goal_name: string;
  goal_value: number;
  goal_type: 'over' | 'under' | 'target' | 'range' | 'boolean';
  variance_percent?: number;
  floor_value?: number;
  unit: string;
  category: string;
}

interface HighlightStats {
  current: number;
  max: number;
  remaining: number;
}

interface GoalManagementProps {
  userId?: string; // If provided, manage goals for this user. If not, use current authenticated user
  readOnly?: boolean; // If true, show read-only view
  onGoalUpdate?: () => void; // Callback when goals change
  hideHeader?: boolean; // If true, hide the internal header (for external header control)
  onHighlightStatsChange?: (stats: HighlightStats) => void; // Callback when highlight stats change
}

export interface GoalManagementRef {
  openAddModal: () => void;
}

// Helper function to format YYYY-MM-DD as MM/DD/YY
const formatDateString = (dateString: string): string => {
  const [year, month, day] = dateString.split('-');
  return `${month}/${day}/${year.slice(-2)}`;
};

export const GoalManagement = forwardRef<GoalManagementRef, GoalManagementProps>(({
  userId,
  readOnly = false,
  onGoalUpdate,
  hideHeader = false,
  onHighlightStatsChange
}, ref) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [templates, setTemplates] = useState<GoalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [highlightStats, setHighlightStats] = useState<{ current: number; max: number; remaining: number } | null>(null);

  // Expose openAddModal to parent via ref
  useImperativeHandle(ref, () => ({
    openAddModal: () => setShowAddModal(true)
  }));
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedGoalForProgress, setSelectedGoalForProgress] = useState<Goal | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (userId) {
      headers['x-user-id'] = userId;
    }
    return headers;
  };

  useEffect(() => {
    loadGoals();
    if (!showAddModal) {
      loadTemplates();
    }
  }, [userId]);

  useEffect(() => {
    if (showAddModal) {
      loadTemplates();
    }
  }, [showAddModal]);

  const loadGoals = async () => {
    try {
      setLoading(true);

      // If userId is provided, use coach endpoint (coach viewing client goals)
      const endpoint = userId
        ? `/api/coach/clients/${userId}/goals`
        : '/api/goals';

      const response = await fetch(endpoint, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success) {
        // Convert goals object/array to array
        const goalsArray = Array.isArray(data.goals)
          ? data.goals
          : Object.entries(data.goals || {}).map(([id, goal]: [string, any]) => ({
              goal_id: id,
              ...goal
            }));
        setGoals(goalsArray);

        // Capture highlight stats if available
        if (data.highlight_stats) {
          setHighlightStats(data.highlight_stats);
          // Notify parent of highlight stats change
          if (onHighlightStatsChange) {
            onHighlightStatsChange(data.highlight_stats);
          }
        }
      } else {
        setError(data.error || 'Failed to load goals');
      }
    } catch (err) {
      console.error('Error loading goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/goals/templates', {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success) {
        // Convert templates object to array
        const templatesArray = Object.entries(data.available_templates || {}).map(([id, template]: [string, any]) => ({
          template_id: id,
          ...template
        }));
        setTemplates(templatesArray);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const handleCreateGoal = async (templateId: string, customValue?: number, goalType?: string, variancePercent?: number, floorValue?: number) => {
    try {
      const body: any = { template_id: templateId };
      if (customValue !== undefined) {
        body.goal_value = customValue;
      }
      if (goalType !== undefined) {
        body.goal_type = goalType;
      }
      if (variancePercent !== undefined) {
        body.variance_percent = variancePercent;
      }
      if (floorValue !== undefined) {
        body.floor_value = floorValue;
      }

      const response = await fetch('/api/goals', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        await loadGoals();
        setShowAddModal(false);
        if (onGoalUpdate) onGoalUpdate();
      } else {
        setError(data.error || 'Failed to create goal');
      }
    } catch (err) {
      console.error('Error creating goal:', err);
      setError('Failed to create goal');
    }
  };

  const handleUpdateGoal = async (goalId: string, updates: Partial<Goal>) => {
    try {
      const response = await fetch(`/api/goals/${goalId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      if (data.success) {
        // STOPGAP: Per-component email toast — pending systemic API wrapper refactor
        // See Kanban card for full cleanup plan (includes AllNotesWorkspace, ActionItemModal, GoalManagement)
        if (data.emailSent === true) {
          toast.success('Goal updated and client notified');
        } else if (data.emailSent === false) {
          toast.error(`Goal updated. Email delivery failed: ${data.emailError || 'Unknown error'}`);
        }
        await loadGoals();
        setEditingGoal(null);
        if (onGoalUpdate) onGoalUpdate();
      } else {
        setError(data.error || 'Failed to update goal');
      }
    } catch (err) {
      console.error('Error updating goal:', err);
      setError('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    if (!confirm('Are you sure you want to delete this goal?')) return;

    try {
      const response = await fetch(`/api/goals/${goalId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        await loadGoals();
        if (onGoalUpdate) onGoalUpdate();
      } else {
        setError(data.error || 'Failed to delete goal');
      }
    } catch (err) {
      console.error('Error deleting goal:', err);
      setError('Failed to delete goal');
    }
  };

  const handleToggleVisibility = async (goalId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent goal progress modal from opening

    try {
      const response = await fetch(`/api/goals/${goalId}/visibility`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        await loadGoals();
        if (onGoalUpdate) onGoalUpdate();
      } else {
        setError(data.error || 'Failed to toggle visibility');
      }
    } catch (err) {
      console.error('Error toggling visibility:', err);
      setError('Failed to toggle visibility');
    }
  };

  const handleToggleStatus = async (goalId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent goal progress modal from opening

    try {
      const response = await fetch(`/api/goals/${goalId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        await loadGoals();
        if (onGoalUpdate) onGoalUpdate();
      } else {
        setError(data.error || 'Failed to toggle status');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status');
    }
  };

  const handleToggleHighlight = async (goal: Goal, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent goal progress modal from opening

    // Only allow for nutrition goals
    if (goal.category !== 'nutrition') return;

    try {
      const response = await fetch(`/api/goals/${goal.goal_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ highlight_for_client: !goal.highlight_for_client })
      });

      const data = await response.json();

      if (data.success) {
        await loadGoals();
        if (onGoalUpdate) onGoalUpdate();
      } else {
        setError(data.error || 'Failed to toggle highlight');
      }
    } catch (err) {
      console.error('Error toggling highlight:', err);
      setError('Failed to toggle highlight');
    }
  };

  const getGoalTypeIcon = (goalType: string) => {
    switch (goalType) {
      case 'over':
        return <TrendingUp size={16} />;
      case 'under':
        return <TrendingDown size={16} />;
      case 'target':
        return <Target size={16} />;
      case 'range':
        return <ArrowLeftRight size={16} />;
      case 'boolean':
        return <Check size={16} />;
      default:
        return <Minus size={16} />;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      nutrition: '#4CAF50',
      fitness: '#2196F3',
      wellness: '#9C27B0',
      supplements: '#FF9800',
      other: '#607D8B'
    };
    return colors[category] || colors.other;
  };

  // Group goals by category in a defined order
  const categoryOrder = ['nutrition', 'fitness', 'wellness', 'supplements', 'other'];

  const groupedGoals = categoryOrder
    .map(category => ({
      category,
      goals: goals.filter(g => g.category === category)
    }))
    .filter(group => group.goals.length > 0);

  if (loading) {
    return (
      <div className="goal-management-container">
        <div className="loading-state">Loading goals...</div>
      </div>
    );
  }

  return (
    <div className="goal-management-container">
      {!hideHeader && (
        <div className="goal-management-header">
          <div className="header-content">
            <Target size={24} />
            <h2>Goal Management</h2>
            {highlightStats && (
              <span className="highlight-counter" title="Goals highlighted on client's This Week tile">
                <Eye size={14} /> {highlightStats.current}/{highlightStats.max}
              </span>
            )}
          </div>
          {!readOnly && (
            <button
              className="btn primary"
              onClick={() => setShowAddModal(true)}
            >
              <Plus size={16} />
              Add Goal
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="goals-grid">
          <div className="empty-state">
            <Target size={48} />
            <p>No goals set yet</p>
            {!readOnly && (
              <button
                className="btn primary"
                onClick={() => setShowAddModal(true)}
              >
                Create Your First Goal
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="goals-by-category">
          {groupedGoals.map((group, _groupIndex) => (
            <div key={group.category} className="category-section">
              <div className="goals-grid">
                {group.goals.map(goal => (
                  <div
                    key={goal.goal_id}
                    className={`goal-card ${goal.status === 'disabled' ? 'disabled' : ''}`}
                    style={{ borderLeftColor: getCategoryColor(goal.category) }}
                  >
                    <div
                      className="goal-card-header clickable"
                      onClick={() => setSelectedGoalForProgress(goal)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view progress"
                    >
                      <div className="goal-info">
                        <div className="goal-type-icon">
                          {getGoalTypeIcon(goal.goal_type)}
                        </div>
                        <div>
                          <h3>{goal.goal_name}</h3>
                          <span className="goal-category">{goal.category}</span>
                        </div>
                      </div>
                      {!readOnly && (
                        <div className="goal-actions" onClick={(e) => e.stopPropagation()}>
                          {goal.category === 'nutrition' && (() => {
                            const atLimit = highlightStats && highlightStats.remaining <= 0 && !goal.highlight_for_client;
                            return (
                              <button
                                className={`action-btn highlight-btn ${goal.highlight_for_client ? 'active' : ''} ${atLimit ? 'disabled' : ''}`}
                                onClick={(e) => !atLimit && handleToggleHighlight(goal, e)}
                                title={atLimit
                                  ? `Maximum ${highlightStats?.max} highlights reached`
                                  : goal.highlight_for_client
                                    ? 'Hide from client weekly summary'
                                    : 'Show on client weekly summary'
                                }
                                disabled={atLimit}
                              >
                                {goal.highlight_for_client ? <Eye size={16} /> : <EyeOff size={16} />}
                              </button>
                            );
                          })()}
                          <button
                            className="action-btn"
                            onClick={() => setEditingGoal(goal)}
                            title="Edit Goal"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="action-btn danger"
                            onClick={() => handleDeleteGoal(goal.goal_id)}
                            title="Delete Goal"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="goal-details">
                      <div className="goal-value">
                        <span className="value">{goal.goal_value}</span>
                        <span className="unit">{goal.unit}</span>
                      </div>
                      <div className="goal-type-label">
                        {goal.goal_type === 'target' && goal.variance_percent && (
                          <span className="variance">±{goal.variance_percent}%</span>
                        )}
                        {goal.goal_type === 'range' && goal.floor_value !== undefined && (
                          <span className="variance">{goal.floor_value}-{goal.goal_value}</span>
                        )}
                        <span className="type-badge">{goal.goal_type}</span>
                      </div>
                    </div>

                    <div className="goal-footer">
                      {!readOnly && (
                        <div className="goal-toggle-buttons">
                          <button
                            className={`toggle-btn visibility-toggle ${goal.visibility}`}
                            onClick={(e) => handleToggleVisibility(goal.goal_id, e)}
                            title={`Toggle visibility (currently ${goal.visibility})`}
                          >
                            {goal.visibility === 'hidden' ? 'HIDDEN' : 'VISIBLE'}
                          </button>
                          <button
                            className={`toggle-btn status-toggle ${goal.status}`}
                            onClick={(e) => handleToggleStatus(goal.goal_id, e)}
                            title={`Toggle status (currently ${goal.status})`}
                          >
                            {goal.status === 'disabled' ? 'DISABLED' : 'ENABLED'}
                          </button>
                        </div>
                      )}
                      <span className="goal-date">
                        {goal.date_modified ? (
                          <>Updated: {formatDateString(goal.date_modified)}</>
                        ) : (
                          <>Created: {formatDateString(goal.created_date)}</>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Goal Modal */}
      {showAddModal && (
        <AddGoalModal
          templates={templates}
          existingGoals={goals}
          onClose={() => setShowAddModal(false)}
          onCreate={handleCreateGoal}
        />
      )}

      {/* Edit Goal Modal */}
      {editingGoal && (
        <EditGoalModal
          goal={editingGoal}
          onClose={() => setEditingGoal(null)}
          onUpdate={handleUpdateGoal}
        />
      )}

      {/* Goal Progress Modal */}
      {selectedGoalForProgress && (
        <GoalProgressModal
          goal={selectedGoalForProgress}
          isOpen={!!selectedGoalForProgress}
          onClose={() => setSelectedGoalForProgress(null)}
          userId={userId}
        />
      )}
    </div>
  );
});

// Add Goal Modal Component
const AddGoalModal: React.FC<{
  templates: GoalTemplate[];
  existingGoals: Goal[];
  onClose: () => void;
  onCreate: (templateId: string, customValue?: number, goalType?: string, variancePercent?: number, floorValue?: number) => void;
}> = ({ templates, existingGoals, onClose, onCreate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<GoalTemplate | null>(null);
  const [customValue, setCustomValue] = useState<string>('');
  const [goalType, setGoalType] = useState<'over' | 'under' | 'target' | 'range'>('under');
  const [variancePercent, setVariancePercent] = useState<string>('');
  const [floorValue, setFloorValue] = useState<string>('');

  // Filter out templates that already have assigned goals (regardless of status)
  const activeTemplateIds = new Set(
    existingGoals
      .map(g => {
        // Extract template_id from goal_id (format: "template_id_userId_timestamp")
        const parts = g.goal_id.split('_');
        // Return all parts except the last two (userId and timestamp)
        return parts.slice(0, -2).join('_');
      })
  );

  // Define mutually exclusive goal pairs
  // If one is active, both should be filtered out
  const mutuallyExclusiveGroups = [
    ['daily_potassium', 'daily_potassium_limit']
    // Add more groups here as needed in the future
  ];

  const availableTemplates = templates.filter(t => {
    // Filter out already active goals
    if (activeTemplateIds.has(t.template_id)) {
      return false;
    }

    // Filter out mutually exclusive goals
    for (const group of mutuallyExclusiveGroups) {
      if (group.includes(t.template_id)) {
        // Check if ANY goal in this group is active
        const hasActiveInGroup = group.some(templateId => activeTemplateIds.has(templateId));
        if (hasActiveInGroup) {
          return false; // Filter out this template
        }
      }
    }

    return true;
  });

  const handleTemplateSelect = (template: GoalTemplate) => {
    setSelectedTemplate(template);
    // Set defaults from template
    setGoalType(template.goal_type as 'over' | 'under' | 'target' | 'range');
    setVariancePercent(template.variance_percent?.toString() || '10');
    setFloorValue(template.floor_value?.toString() || '');
  };

  const handleSubmit = () => {
    if (!selectedTemplate) return;

    const value = customValue ? parseFloat(customValue) : undefined;
    const variance = goalType === 'target' && variancePercent ? parseFloat(variancePercent) : undefined;
    const floor = goalType === 'range' && floorValue ? parseFloat(floorValue) : undefined;
    onCreate(selectedTemplate.template_id, value, goalType, variance, floor);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add New Goal"
      variant="info"
      size="medium"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!selectedTemplate}
          >
            Create Goal
          </button>
        </>
      }
    >
      <div className="form-group">
        <label>Select Goal Template</label>
        <div className="template-list">
          {availableTemplates.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              All available goals are already active
            </div>
          ) : (
            availableTemplates.map(template => (
              <div
                key={template.template_id}
                className={`template-item ${selectedTemplate?.template_id === template.template_id ? 'selected' : ''}`}
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="template-info">
                  <strong>{template.goal_name}</strong>
                  <span className="template-meta">
                    {template.goal_value} {template.unit} ({template.goal_type})
                  </span>
                </div>
                {selectedTemplate?.template_id === template.template_id && (
                  <Check size={20} />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {selectedTemplate && (
        <>
          <div className="form-group">
            <label>Goal Type</label>
            <select
              value={goalType}
              onChange={e => setGoalType(e.target.value as 'over' | 'under' | 'target' | 'range')}
              className="form-select"
            >
              <option value="under">Under (stay below target)</option>
              <option value="over">Over (meet or exceed target)</option>
              <option value="target">Target (hit within variance %)</option>
              <option value="range">Range (stay within floor-ceiling)</option>
            </select>
          </div>

          <div className="form-group">
            <label>{goalType === 'range' ? 'Ceiling Value (upper bound)' : 'Target Value'}</label>
            <div className="input-with-unit">
              <input
                type="number"
                step="0.1"
                placeholder={`Default: ${selectedTemplate.goal_value}`}
                value={customValue}
                onChange={e => setCustomValue(e.target.value)}
                className="form-input"
              />
              <span className="unit-label">{selectedTemplate.unit}</span>
            </div>
          </div>

          {goalType === 'target' && (
            <div className="form-group">
              <label>Variance Percent (±)</label>
              <input
                type="number"
                step="1"
                value={variancePercent}
                onChange={e => setVariancePercent(e.target.value)}
                className="form-input"
                placeholder="e.g., 10 for ±10%"
              />
            </div>
          )}

          {goalType === 'range' && (
            <div className="form-group">
              <label>Floor Value (lower bound)</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  step="0.1"
                  value={floorValue}
                  onChange={e => setFloorValue(e.target.value)}
                  className="form-input"
                  placeholder="e.g., 100"
                />
                <span className="unit-label">{selectedTemplate?.unit}</span>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

// Edit Goal Modal Component
const EditGoalModal: React.FC<{
  goal: Goal;
  onClose: () => void;
  onUpdate: (goalId: string, updates: Partial<Goal>) => void;
}> = ({ goal, onClose, onUpdate }) => {
  const [goalValue, setGoalValue] = useState(goal.goal_value.toString());
  const [variancePercent, setVariancePercent] = useState(goal.variance_percent?.toString() || '');
  const [editFloorValue, setEditFloorValue] = useState(goal.floor_value?.toString() || '');
  const [status, setStatus] = useState(goal.status);
  const [highlightForClient, setHighlightForClient] = useState(goal.highlight_for_client || false);

  // Only show highlight checkbox for nutrition goals
  const isNutritionGoal = goal.category === 'nutrition';

  const handleSubmit = () => {
    const updates: Partial<Goal> = {
      goal_value: parseFloat(goalValue),
      status
    };

    if (goal.goal_type === 'target' && variancePercent) {
      updates.variance_percent = parseFloat(variancePercent);
    }

    if (goal.goal_type === 'range' && editFloorValue) {
      updates.floor_value = parseFloat(editFloorValue);
    }

    // Only include highlight_for_client for nutrition goals
    if (isNutritionGoal) {
      updates.highlight_for_client = highlightForClient;
    }

    onUpdate(goal.goal_id, updates);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Edit Goal: ${goal.goal_name}`}
      variant="info"
      size="small"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Update Goal
          </button>
        </>
      }
    >
      <div className="form-group">
        <label>{goal.goal_type === 'range' ? 'Ceiling Value (upper bound)' : 'Target Value'}</label>
        <div className="input-with-unit">
          <input
            type="number"
            step="0.1"
            value={goalValue}
            onChange={e => setGoalValue(e.target.value)}
            className="form-input"
          />
          <span className="unit-label">{goal.unit}</span>
        </div>
      </div>

      {goal.goal_type === 'target' && (
        <div className="form-group">
          <label>Variance Percent (±)</label>
          <input
            type="number"
            step="1"
            value={variancePercent}
            onChange={e => setVariancePercent(e.target.value)}
            className="form-input"
            placeholder="e.g., 5 for ±5%"
          />
        </div>
      )}

      {goal.goal_type === 'range' && (
        <div className="form-group">
          <label>Floor Value (lower bound)</label>
          <div className="input-with-unit">
            <input
              type="number"
              step="0.1"
              value={editFloorValue}
              onChange={e => setEditFloorValue(e.target.value)}
              className="form-input"
              placeholder="e.g., 100"
            />
            <span className="unit-label">{goal.unit}</span>
          </div>
        </div>
      )}

      <div className="form-group">
        <label>Status</label>
        <select
          value={status}
          onChange={e => setStatus(e.target.value as 'enabled' | 'disabled')}
          className="form-select"
        >
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {isNutritionGoal && (
        <div className="form-group highlight-checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={highlightForClient}
              onChange={e => setHighlightForClient(e.target.checked)}
            />
            <span>Show on client weekly summary</span>
          </label>
          <p className="form-help-text">
            Highlights this goal on the client's "This Week" tile
          </p>
        </div>
      )}
    </Modal>
  );
};

export default GoalManagement;
